import React, { useMemo, useCallback } from "react";
import { usePublication } from "../../../hooks/usePublication";
import { Section, SubSection } from "../../../interfaces/Publication";
import Divider from "./Divider";
import Figure from "./Figure";
import FragmentContent from "./FragmentContent";
import BtnDataSheet from "./BtnDataSheet";
import Anchor from "./Anchor";
import parse from "html-react-parser";
import { ButtonsRight } from "../../../interfaces/UI";
import { useTrivia } from "../../../hooks";
import HighlightableText from "./HighlatableText";
import { PodcastButton } from "./PlayerButton";
import { convertKatexToImages } from "../../utils/mathUtils";
import { usePodcastPlayer } from "../../../context/player/Player";

interface SubSectionContentProps {
  subSection: SubSection;
  openButtons: ButtonsRight;
  setOpenButtons: (state: ButtonsRight) => void;
  section: Section;
  index: number;
}

const countWordsInHtml = (html: string): number => {
  if (!html) return 0;

  // Process KaTeX first, just like TextHighlighter does
  const processedHtml = convertKatexToImages(html, {
    dpi: 180,
    backgroundColor: "transparent",
  });

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = processedHtml;

  // Merge sub/sup elements - SAME AS TextHighlighter
  const mergeSubSup = (element: Element) => {
    const subs = element.querySelectorAll("sub, sup");
    subs.forEach((subSup) => {
      const prev = subSup.previousSibling;
      const next = subSup.nextSibling;

      // Build the merged text: prev + sub/sup + next
      let mergedText = "";

      if (prev && prev.nodeType === Node.TEXT_NODE) {
        mergedText = prev.textContent || "";
      }

      mergedText += subSup.textContent || "";

      if (next && next.nodeType === Node.TEXT_NODE) {
        mergedText += next.textContent || "";
        next.remove(); // Remove the next node
      }

      // Update or create the merged text node
      if (prev && prev.nodeType === Node.TEXT_NODE) {
        prev.textContent = mergedText;
      } else if (subSup.parentNode) {
        // No previous text node, create one
        const textNode = document.createTextNode(mergedText);
        subSup.parentNode.insertBefore(textNode, subSup);
      }

      subSup.remove();
    });
  };

  mergeSubSup(tempDiv);

  // Use TreeWalker to get text nodes, same as TextHighlighter
  const walker = document.createTreeWalker(tempDiv, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => {
      const parent = node.parentElement as HTMLElement;
      if (
        parent?.hasAttribute("data-ignore-highlight") ||
        parent?.closest('[data-ignore-highlight="true"]')
      ) {
        return NodeFilter.FILTER_REJECT;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  });

  let wordCount = 0;
  let node: Node | null;

  while ((node = walker.nextNode())) {
    const text = node.textContent || "";
    if (!text.trim()) continue;

    // Split and count words exactly like TextHighlighter
    const parts = text.split(/\s+/);
    parts.forEach((part) => {
      const trimmed = part.trim();
      if (trimmed && /[A-Za-zÁÉÍÓÚáéíóúÑñÜü0-9]/.test(trimmed)) {
        wordCount++;
      }
    });
  }

  return wordCount;
};

// Calcular offsets para todos los fragments TXT de la subsección
const calculateTextOffsets = (subSection: SubSection, isPlaying: boolean) => {
  let currentOffset = 0;
  const fragmentOffsets: { [fragmentId: string]: number } = {};

  // If audio is playing AND subsection.text exists, include it as virtual fragment 0
  if (isPlaying && subSection.text && subSection.text !== "<p><br></p>") {
    const virtualFragmentId = `subsection-${subSection.id}`;
    fragmentOffsets[virtualFragmentId] = currentOffset;

    const wordCount = countWordsInHtml(subSection.text);
    console.log(
      `📊 Subsection ${subSection.id} text (virtual fragment): offset=${currentOffset}, words=${wordCount}`
    );

    currentOffset += wordCount;
  }

  // Process regular TXT fragments
  const textFragments = subSection.fragments.filter((f) => f.type === "TXT");

  textFragments.forEach((fragment, idx) => {
    fragmentOffsets[fragment.id] = currentOffset;

    const wordCount = countWordsInHtml(fragment.text || "");
    console.log(
      `  Fragment ${idx} (id: ${fragment.id}): offset=${currentOffset}, words=${wordCount}`
    );

    currentOffset += wordCount;
  });

  console.log("✅ Total words in subsection:", currentOffset);

  return fragmentOffsets;
};

const SubSectionContent = React.memo(
  ({
    subSection,
    openButtons,
    setOpenButtons,
    section,
    index,
  }: SubSectionContentProps) => {
    const { path } = usePublication();
    const { openModalTrivia, setCurrentTriviaId } = useTrivia();
    const { playerState } = usePodcastPlayer();

    const hasUrlFicha = !!subSection.urlFicha;
    const hasCover = !!subSection.cover;
    const hasAudio = !!subSection.audio;
    const hasAudio2 = !!subSection.audio2;
    const hasText = subSection.text && subSection.text !== "<p><br></p>";

    const memoizedFragments = useMemo(
      () => subSection.fragments || [],
      [subSection.fragments]
    );

    const parsedText = useMemo(
      () => (hasText ? parse(subSection.text!) : null),
      [hasText, subSection.text]
    );

    // Check if this subsection's audio is currently playing
    const isThisSubsectionPlaying = useMemo(() => {
      return (
        playerState.isPlaying &&
        playerState.currentSubsectionId === subSection.id.toString()
      );
    }, [playerState.isPlaying, playerState.currentSubsectionId, subSection.id]);

    // Calcular offsets - include subsection.text only when playing
    const fragmentOffsets = useMemo(
      () => calculateTextOffsets(subSection, isThisSubsectionPlaying),
      [subSection, isThisSubsectionPlaying]
    );

    const openTrivia = useCallback(
      (trivia: number) => {
        const overlay = document.querySelector(".js-backdrop");
        overlay?.classList.add("visible-fadein");
        setCurrentTriviaId(trivia || 0);
        openModalTrivia();
      },
      [openModalTrivia, setCurrentTriviaId]
    );

    const shouldShowPodcastButton = hasAudio || hasAudio2;

    return (
      <>
        <Anchor name={`C${subSection.id.toString()}`} />
        <section className="article acontent" data-scrollspy>
          {hasCover && <Figure path={`${path}images/${subSection.cover}`} />}

          {memoizedFragments.length > 0 && (
            <article
              className="subsection__article"
              style={{ marginBottom: 10 }}
            >
              {hasUrlFicha && <BtnDataSheet subSection={subSection} />}
              {subSection.title && <h3>{parse(subSection.title)}</h3>}

              {shouldShowPodcastButton && (
                <div
                  style={{
                    position: "relative",
                    height: "50px",
                    margin: "10px 0",
                  }}
                >
                  <PodcastButton
                    podcastUrl={subSection.audio || ""}
                    podcastUrl2={subSection.audio2 || ""}
                    title={subSection.title}
                    subsectionId={subSection.id.toString()}
                  />
                </div>
              )}

              {subSection.abstract && <h4>{subSection.abstract}</h4>}

              {/* Conditionally render subsection.text based on playing state */}
              {parsedText && !isThisSubsectionPlaying && (
                <HighlightableText
                  htmlContent={subSection.text!}
                  fragmentId={`subsection-${subSection.id}`}
                  className="p-container gcontent first-para"
                />
              )}

              {/* When playing, render subsection.text as a TXT fragment */}
              {hasText && isThisSubsectionPlaying && (
                <FragmentContent
                  fragment={{
                    id: `subsection-${subSection.id}` as any,
                    type: "TXT" as any,
                    title: "",
                    text: subSection.text,
                    text2: "",
                    img: "",
                    img2: "",
                    urlObj: "",
                    urlObj2: "",
                    objs: [],
                  }}
                  openButtons={openButtons}
                  setOpenButtons={setOpenButtons}
                  index={-1}
                  textOffset={
                    fragmentOffsets[`subsection-${subSection.id}`] || 0
                  }
                  subsectionId={subSection.id}
                />
              )}
            </article>
          )}

          {/* Render regular fragments */}
          {memoizedFragments.map((fragment, fragmentIndex) => (
            <FragmentContent
              key={fragment.id}
              fragment={fragment}
              openButtons={openButtons}
              setOpenButtons={setOpenButtons}
              index={fragmentIndex}
              textOffset={fragmentOffsets[fragment.id]}
              subsectionId={subSection.id}
            />
          ))}
        </section>

        {section.subsections &&
          section.trivia &&
          index === section.subsections?.length - 1 && (
            <div>
              <button
                type="button"
                className="btn cta-primary-btn"
                data-modal-trigger="trivia"
                aria-label="Cerrar ventana de Trivia"
                onClick={() => {
                  openTrivia(section.trivia || 0);
                }}
              >
                <span>Trivia</span>
              </button>
            </div>
          )}
        <Divider />
      </>
    );
  }
);

export default SubSectionContent;
